import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

const TRANSLATIONS: Record<string, Record<string, string>> = {
  es: {
    title: '¿Qué podemos mejorar?',
    subtitle: 'Tu opinión nos ayuda a ofrecerte un mejor servicio.',
    placeholder: 'Cuéntanos qué ha pasado o cómo podemos mejorar...',
    submit: 'Enviar comentario',
    sending: 'Enviando...',
    thanks_title: '¡Gracias por tu comentario!',
    thanks_msg: 'Revisaremos tu opinión y trabajaremos para mejorar. Si necesitas ayuda inmediata, no dudes en contactarnos.',
    contact: 'info@jvs-informatica.com',
    error: 'Ha ocurrido un error. Inténtalo de nuevo.',
    expired: 'Este enlace ha caducado.',
    expired_msg: 'Si necesitas ayuda, contacta con nosotros en info@jvs-informatica.com.',
    order: 'Pedido',
    your_rating: 'Tu valoración',
    min_chars: 'Mínimo 10 caracteres',
  },
  en: {
    title: 'What can we improve?',
    subtitle: 'Your feedback helps us provide a better service.',
    placeholder: 'Tell us what happened or how we can improve...',
    submit: 'Send feedback',
    sending: 'Sending...',
    thanks_title: 'Thank you for your feedback!',
    thanks_msg: 'We will review your comments and work to improve. If you need immediate help, don\'t hesitate to contact us.',
    contact: 'info@jvs-informatica.com',
    error: 'An error occurred. Please try again.',
    expired: 'This link has expired.',
    expired_msg: 'If you need help, contact us at info@jvs-informatica.com.',
    order: 'Order',
    your_rating: 'Your rating',
    min_chars: 'Minimum 10 characters',
  },
  pt: {
    title: 'O que podemos melhorar?',
    subtitle: 'A sua opinião ajuda-nos a prestar um melhor serviço.',
    placeholder: 'Conte-nos o que aconteceu ou como podemos melhorar...',
    submit: 'Enviar comentário',
    sending: 'A enviar...',
    thanks_title: 'Obrigado pelo seu comentário!',
    thanks_msg: 'Vamos analisar a sua opinião e trabalhar para melhorar. Se precisar de ajuda imediata, não hesite em contactar-nos.',
    contact: 'info@jvs-informatica.com',
    error: 'Ocorreu um erro. Tente novamente.',
    expired: 'Este link expirou.',
    expired_msg: 'Se precisar de ajuda, contacte-nos em info@jvs-informatica.com.',
    order: 'Encomenda',
    your_rating: 'A sua avaliação',
    min_chars: 'Mínimo 10 caracteres',
  },
  fr: {
    title: 'Que pouvons-nous améliorer ?',
    subtitle: 'Votre avis nous aide à offrir un meilleur service.',
    placeholder: 'Dites-nous ce qui s\'est passé ou comment nous pouvons nous améliorer...',
    submit: 'Envoyer le commentaire',
    sending: 'Envoi en cours...',
    thanks_title: 'Merci pour votre commentaire !',
    thanks_msg: 'Nous examinerons votre avis et travaillerons à l\'amélioration. Si vous avez besoin d\'aide immédiate, n\'hésitez pas à nous contacter.',
    contact: 'info@jvs-informatica.com',
    error: 'Une erreur est survenue. Veuillez réessayer.',
    expired: 'Ce lien a expiré.',
    expired_msg: 'Si vous avez besoin d\'aide, contactez-nous à info@jvs-informatica.com.',
    order: 'Commande',
    your_rating: 'Votre évaluation',
    min_chars: 'Minimum 10 caractères',
  },
  de: {
    title: 'Was können wir verbessern?',
    subtitle: 'Ihr Feedback hilft uns, einen besseren Service zu bieten.',
    placeholder: 'Erzählen Sie uns, was passiert ist oder wie wir uns verbessern können...',
    submit: 'Feedback senden',
    sending: 'Wird gesendet...',
    thanks_title: 'Vielen Dank für Ihr Feedback!',
    thanks_msg: 'Wir werden Ihre Meinung prüfen und an Verbesserungen arbeiten. Wenn Sie sofortige Hilfe benötigen, zögern Sie nicht, uns zu kontaktieren.',
    contact: 'info@jvs-informatica.com',
    error: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
    expired: 'Dieser Link ist abgelaufen.',
    expired_msg: 'Wenn Sie Hilfe benötigen, kontaktieren Sie uns unter info@jvs-informatica.com.',
    order: 'Bestellung',
    your_rating: 'Ihre Bewertung',
    min_chars: 'Mindestens 10 Zeichen',
  },
}

const RATING_EMOJIS = ['😡', '😕', '😐', '😊', '😍']

export default function FeedbackPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''

  const [lang, setLang] = useState('es')
  const [orderId, setOrderId] = useState('')
  const [rating, setRating] = useState<number | null>(null)
  const [feedback, setFeedback] = useState('')
  const [status, setStatus] = useState<'loading' | 'form' | 'sending' | 'sent' | 'expired' | 'error'>('loading')
  const [errorMsg, setErrorMsg] = useState('')

  const t = TRANSLATIONS[lang] || TRANSLATIONS.es

  useEffect(() => {
    if (!token) {
      setStatus('expired')
      return
    }
    fetch(`/api/review-gate/rate-info?token=${encodeURIComponent(token)}`)
      .then(res => {
        if (!res.ok) throw new Error('Invalid token')
        return res.json()
      })
      .then(data => {
        setLang(data.lang_iso || 'es')
        setOrderId(data.order_id || '')
        setRating(data.rating)
        if (data.has_feedback) {
          setStatus('sent')
        } else {
          setStatus('form')
        }
      })
      .catch(() => {
        setStatus('expired')
      })
  }, [token])

  const handleSubmit = async () => {
    if (feedback.trim().length < 10) return
    setStatus('sending')
    try {
      const res = await fetch('/api/review-gate/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, feedback_text: feedback.trim() }),
      })
      if (!res.ok) throw new Error('Failed')
      setStatus('sent')
    } catch {
      setErrorMsg(t.error)
      setStatus('error')
    }
  }

  return (
    <div style={{
      margin: 0,
      padding: 0,
      fontFamily: 'Arial, sans-serif',
      background: '#f5f5f5',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
    }}>
      <div style={{
        background: 'white',
        borderRadius: 12,
        padding: '32px 28px',
        maxWidth: 460,
        width: '90%',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img
            src="https://www.jvs-informatica.com/img/jvs-informatica-logo-1621311126.jpg"
            alt="JVS Informática"
            style={{ maxWidth: 180, height: 'auto' }}
          />
        </div>

        {status === 'loading' && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#666' }}>
            <div style={{
              display: 'inline-block',
              width: 32,
              height: 32,
              border: '3px solid #e0e0e0',
              borderTop: '3px solid #3498db',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }} />
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {status === 'expired' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>⏰</div>
            <h2 style={{ color: '#333', fontSize: 20, margin: '0 0 8px' }}>{t.expired}</h2>
            <p style={{ color: '#666', fontSize: 14, lineHeight: 1.6 }}>
              {t.expired_msg}
            </p>
          </div>
        )}

        {(status === 'form' || status === 'error') && (
          <>
            {/* Rating display */}
            {rating && (
              <div style={{
                textAlign: 'center',
                marginBottom: 20,
                padding: '12px 16px',
                background: '#fff3e0',
                borderRadius: 8,
                border: '1px solid #ffe0b2',
              }}>
                <span style={{ fontSize: 12, color: '#999' }}>{t.order} #{orderId}</span>
                <div style={{ marginTop: 4 }}>
                  <span style={{ fontSize: 11, color: '#666' }}>{t.your_rating}: </span>
                  <span style={{ fontSize: 28 }}>{RATING_EMOJIS[rating - 1]}</span>
                </div>
              </div>
            )}

            <h2 style={{ color: '#333', fontSize: 20, margin: '0 0 8px', textAlign: 'center' }}>
              {t.title}
            </h2>
            <p style={{ color: '#666', fontSize: 13, textAlign: 'center', margin: '0 0 20px', lineHeight: 1.5 }}>
              {t.subtitle}
            </p>

            <textarea
              value={feedback}
              onChange={e => setFeedback(e.target.value)}
              placeholder={t.placeholder}
              rows={5}
              style={{
                width: '100%',
                padding: 12,
                border: '1px solid #ddd',
                borderRadius: 8,
                fontSize: 14,
                fontFamily: 'Arial, sans-serif',
                resize: 'vertical',
                boxSizing: 'border-box',
                outline: 'none',
              }}
              onFocus={e => e.target.style.borderColor = '#3498db'}
              onBlur={e => e.target.style.borderColor = '#ddd'}
            />
            {feedback.length > 0 && feedback.trim().length < 10 && (
              <p style={{ color: '#e74c3c', fontSize: 11, margin: '4px 0 0' }}>{t.min_chars}</p>
            )}

            {errorMsg && (
              <p style={{ color: '#e74c3c', fontSize: 13, margin: '8px 0 0', textAlign: 'center' }}>{errorMsg}</p>
            )}

            <button
              onClick={handleSubmit}
              disabled={feedback.trim().length < 10}
              style={{
                width: '100%',
                padding: '12px 24px',
                marginTop: 16,
                background: feedback.trim().length >= 10 ? '#3498db' : '#ccc',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontSize: 15,
                fontWeight: 'bold',
                cursor: feedback.trim().length >= 10 ? 'pointer' : 'not-allowed',
              }}
            >
              {t.submit}
            </button>
          </>
        )}

        {status === 'sending' && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#666' }}>
            <div style={{
              display: 'inline-block',
              width: 32,
              height: 32,
              border: '3px solid #e0e0e0',
              borderTop: '3px solid #3498db',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }} />
            <p style={{ marginTop: 12, fontSize: 14 }}>{t.sending}</p>
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {status === 'sent' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>💙</div>
            <h2 style={{ color: '#333', fontSize: 20, margin: '0 0 8px' }}>{t.thanks_title}</h2>
            <p style={{ color: '#666', fontSize: 14, lineHeight: 1.6 }}>{t.thanks_msg}</p>
            <p style={{ marginTop: 16 }}>
              <a href={`mailto:${t.contact}`} style={{ color: '#3498db', fontSize: 13 }}>{t.contact}</a>
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
